import { MutationCache, QueryCache } from '@tanstack/react-query';
import type { SupportLogServiceName } from './support-logging.types';
import { logger } from './support-logger';

interface SupportCacheConfig {
  serviceName: SupportLogServiceName;
  source?: string;
}

function safeSerializeKey(key: unknown): string {
  try {
    return JSON.stringify(key);
  } catch {
    return String(key);
  }
}

function currentPathname(): string {
  return typeof window !== 'undefined' ? window.location.pathname : '';
}

export function createSupportLoggingQueryCache(config: SupportCacheConfig): QueryCache {
  return new QueryCache({
    onError: (error, query) => {
      if (query?.meta?.['silent']) return;
      logger?.logError({
        eventName: 'fe.query_error',
        serviceName: config.serviceName,
        source: config.source,
        description: `[${currentPathname()}] ${error instanceof Error ? error.message : String(error)}`,
        error,
        extra: { queryKey: safeSerializeKey(query.queryKey) },
      });
    },
  });
}

export function createSupportLoggingMutationCache(config: SupportCacheConfig): MutationCache {
  return new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation?.meta?.['silent']) return;
      logger?.logError({
        eventName: 'fe.mutation_error',
        serviceName: config.serviceName,
        source: config.source,
        description: `[${currentPathname()}] ${error instanceof Error ? error.message : String(error)}`,
        error,
        ...(mutation.options.mutationKey !== undefined
          ? { extra: { mutationKey: safeSerializeKey(mutation.options.mutationKey) } }
          : {}),
      });
    },
  });
}
